/** カテゴリのRepository（仕様書 §5.3・§15.2）。削除は論理削除のみ。 */

import { getDb } from './schema';
import type { Category } from '../domain/types';
import { newId } from '../domain/ids';
import { enqueue } from './outbox';
import { bumpData } from './events';

export const DEFAULT_CATEGORIES: Category[] = [
  { id: 'cat-default-diary', name: '日記', color: '#4A90D9', icon: 'book', order: 1, deleted: false },
  { id: 'cat-default-work', name: '仕事', color: '#7B68A6', icon: 'work', order: 2, deleted: false },
  { id: 'cat-default-travel', name: '旅行', color: '#2E9E7B', icon: 'pin', order: 3, deleted: false },
  { id: 'cat-default-food', name: '食事', color: '#D98A3B', icon: 'food', order: 4, deleted: false },
];

export const UNCATEGORIZED_LABEL = '未設定';
export const UNCATEGORIZED_COLOR = '#9AA0A6';

export async function listCategories(includeDeleted = false): Promise<Category[]> {
  const db = await getDb();
  const all = await db.getAll('categories');
  return all
    .filter((c) => includeDeleted || !c.deleted)
    .sort((a, b) => a.order - b.order);
}

export async function getCategoryMap(): Promise<Map<string, Category>> {
  const db = await getDb();
  const all = await db.getAll('categories');
  return new Map(all.map((c) => [c.id, c]));
}

export async function ensureDefaults(): Promise<void> {
  const db = await getDb();
  if ((await db.count('categories')) > 0) return;
  const tx = db.transaction('categories', 'readwrite');
  for (const c of DEFAULT_CATEGORIES) await tx.store.put(c);
  await tx.done;
}

export async function saveCategory(c: Omit<Category, 'id'> & { id?: string }): Promise<Category> {
  const db = await getDb();
  const saved: Category = { ...c, id: c.id ?? newId() };
  await db.put('categories', saved);
  await enqueue({ type: 'categories.put', targetKey: 'categories' });
  bumpData();
  return saved;
}

/** 論理削除。記事側の categoryId は書き換えない（§5.3）。 */
export async function softDeleteCategory(id: string): Promise<void> {
  const db = await getDb();
  const c = await db.get('categories', id);
  if (!c) return;
  await db.put('categories', { ...c, deleted: true });
  await enqueue({ type: 'categories.put', targetKey: 'categories' });
  bumpData();
}

export async function restoreCategory(id: string): Promise<void> {
  const db = await getDb();
  const c = await db.get('categories', id);
  if (!c) return;
  await db.put('categories', { ...c, deleted: false });
  await enqueue({ type: 'categories.put', targetKey: 'categories' });
  bumpData();
}

/**
 * 表示用のカテゴリ解決。参照先が存在しない、または論理削除済みなら「未設定」扱い（§5.3）。
 */
export function resolveCategory(
  map: Map<string, Category>,
  categoryId: string | null,
): { name: string; color: string; icon: string } {
  if (categoryId) {
    const c = map.get(categoryId);
    if (c && !c.deleted) return { name: c.name, color: c.color, icon: c.icon };
  }
  return { name: UNCATEGORIZED_LABEL, color: UNCATEGORIZED_COLOR, icon: 'note' };
}

/** 並べ替えの確定。order を振り直して一括保存する。 */
export async function reorderCategories(ordered: Category[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('categories', 'readwrite');
  await Promise.all(ordered.map((c, i) => tx.store.put({ ...c, order: i + 1 })));
  await tx.done;
  await enqueue({ type: 'categories.put', targetKey: 'categories' });
  bumpData();
}
